<template>
    <HconCornerBreadcrumbs :breadcrumbs="breadcrumbs" @click-breadcrumbs="getDepth" :searchFlag="true"/>
    <NuxtPage/>
</template>
<script lang="ts" setup>
import HconCornerBreadcrumbs from '@/components/corner/pc/HconCornerBreadcrumbsPC.vue'; // breadcrumb
import type { RecommendationsearchwordsDto, DisplayMainHeaderInfo, CategoryBrandMappingDto, RecentsearchwordsDto } from "~/types/display/backend-dto-types";
const fetchBuilder = useHconFetchBuilder();
const { openLoginPopup, isLoggedIn, showConfirm, showAlert} = usePopup()
const route = useRoute()
import { onMounted, watch } from 'vue'


interface BreadcrumbItem {
  label : string        // 화면에 보여질 브래드크럼명 ex) 홈 > 마이 Hcon
  to: string            // 브래드크럼명 클릭 시 이동되는 경로(useRoute를 이용하여 자동으로 파일 폴더 경로대로 지정됨) ex) pc/mypage
  child?: any[]         // 하위 자식들 ex) pc/mypage의 경우 해당 경로 하위에 있는 값 들(구매관리, 핀발행 요청 관리, H캐시 관리 등) 
  category?: boolean    // 추후 상품목록에서 카테고리, 브랜드 별로 필터링 시 사용될 값
  hideChild?: boolean   // 하위 자식들 숨김 여부 ex) true => pc/mypage 자식들이 드롭다운으로 안보여짐
  skipParent?: string[] // 중간에 skip할 부모 브래드크럼들 => ex) ['마이 Hcon', '회원 정보 관리'] => 회원탈퇴 완료 페이지에서 해당 부모 브래드크럼을 skip
  movable?: boolean     // 브래드크럼명 클릭 시 이동 여부 => ex) 기획전 목록, 기획전 상세 페이지의 경우 브래드크럼 클릭 시 해당 경로로 이동 x
  id? : string
  depth?: number
  highCategoryId?: string
  categoryId?: string
  brandId? : string
}

interface ProductDetail {
dspGdsCd: string
dspGdsNm: string
gdsNm: string
frstCtgrCd: string
sndCtgrCd: string
brndCd?: string
}


const productDetail = ref<ProductDetail>({
dspGdsCd: "", 
dspGdsNm: "",
gdsNm: "",
frstCtgrCd: "",
sndCtgrCd: "",
brndCd: "",
})
// 홈 경로
const crumbs: BreadcrumbItem[] = [{label: '홈', to:'/pc/product/main'}]

// 카테고리-브랜드 조회 결과 리스트 (햄버거 아이콘 클릭 시 노출)
const categoryBrandList = ref<CategoryBrandMappingDto[]>([]);
const buildCategoryTree = (list: CategoryBrandMappingDto[]) => {
const lvl1Map = new Map();

list.forEach(row => {
  // ======================================================
  // 1 레벨 카테고리 세팅
  // ======================================================
  if (!lvl1Map.has(row.frstCtgrCd)) {
    lvl1Map.set(row.frstCtgrCd, {
      id: row.frstCtgrCd,
      name: row.frstCtgrNm,
      sort: row.frstCtgrSortOrdg,
      to: '/pc/product/search?searchHighCategory='+row.frstCtgrCd + "&t=" + Date.now(),
      children: [],
      brands: [],
      _brandSet: new Set<string>()
    })
  }
  const lvl1 = lvl1Map.get(row.frstCtgrCd);
  // ======================================================
  // 2 레벨 있는 경우 카테고리 세팅
  // ======================================================
  if (row.sndCtgrCd) {
    let lvl2 = lvl1.children.find(
      (c: any) => c.id === row.sndCtgrCd
    )

    if (!lvl2) {
      lvl2 = {
        id: row.sndCtgrCd,
        name: row.sndCtgrNm,
        sort: row.sndCtgrSortOrdg,
        to: '/pc/product/search?searchHighCategory='+row.frstCtgrCd+'&searchCategory='+row.sndCtgrCd + "&t=" + Date.now(),
        brands: [],
        _brandSet: new Set<string>()
      }
      lvl1.children.push(lvl2);
    }

    // ======================================================
    // 브랜드 있는 경우 브랜드 세팅
    // ======================================================
    if (row.brndCd && !lvl2._brandSet.has(row.brndCd)) {
      lvl2._brandSet.add(row.brndCd);
      lvl2.brands.push({
        id: row.brndCd,
        name: row.brndNm,
        sort: row.brndSortOrdg,
        to: '/pc/product/search?searchHighCategory='+row.frstCtgrCd+'&searchCategory='+row.sndCtgrCd+'&searchBrand='+row.brndCd + "&t=" + Date.now(),
      })
    }
  } else {
    // ======================================================
    // 2 레벨 없는 경우 → 1레벨 브랜드 있는 경우 세팅
    // ======================================================
    if (row.brndCd && !lvl1._brandSet.has(row.brndCd)) {
      lvl1._brandSet.add(row.brndCd);
      lvl1.brands.push({
        id: row.brndCd,
        name: row.brndNm,
        sort: row.brndSortOrdg,
        to: '/pc/product/search?searchHighCategory='+row.frstCtgrCd+'searchBrand='+row.brndCd + "&t=" + Date.now()
      })
    }
  }
})

// ======================================================
// 정렬 및 정리
// ======================================================
return Array.from(lvl1Map.values()).sort((a, b) => a.sort - b.sort).map(lvl1 => {
  lvl1.children.sort((a: any, b: any) => a.sort - b.sort);
  lvl1.children.forEach((lvl2: any) => {
    lvl2.brands.sort((a: any, b: any) => a.sort - b.sort);
    delete lvl2._brandSet;
  });

  lvl1.brands.sort((a: any, b: any) => a.sort - b.sort);
  delete lvl1._brandSet;
  return lvl1;
})
}

// 카테고리-브랜드 매핑 트리 데이터 (전체 데이터)
const treeData = computed(() =>  {
return buildCategoryTree(categoryBrandList.value)
});

type Brand = {
id: string
name: string
sort: number
to: string
}

type Depth2 = {
id: string
name: string
sort: number
to: string
children: Brand[]
brands: Brand[]
}

type Depth1 = {
id: string
name: string
sort: number
to: string
children: Depth2[]
brands: Brand[]
}

const currentDepth1 = ref<Depth1 | null>(null);
const currentDepth2 = ref<Depth2 | null>(null);
const currentBrand = ref<Brand | null>(null);

// depth 에 따라 세팅
const depth1Menus = computed(() =>
  treeData.value.map(lvl1 => ({
    label: lvl1.name,
    to: lvl1.to,
    id: lvl1.id,
    depth : 1,
    highCategoryId: lvl1.id
  }))
)


const depth2Menus = computed(() => {
  if(currentDepth1.value?.children.length == 0) return []

  return  currentDepth1.value?.children.map((lvl2) => ({
      label: lvl2.name,
      to: lvl2.to,
      id: lvl2.id,
      depth : 2,
      highCategoryId: currentDepth1.value?.id,
      categoryId :  lvl2.id,
    }))
})

const depth3Menus = computed(() => {
  if (currentDepth2.value) {
    return currentDepth2.value.brands.map((b) => ({
      label: b.name,
      to: b.to,
      id: b.id,
      depth : 3,
      highCategoryId: currentDepth1.value?.id,
      categoryId :  currentDepth2.value?.id,
      brandId : b.id,
    }))
  }

  if (currentDepth1.value) {
    return currentDepth1.value.brands.map((b) => ({
      label: b.name,
      to: b.to,
      id: b.id,
      depth : 3,
      highCategoryId: currentDepth1.value?.id,
      categoryId :  currentDepth2.value?.id,
      brandId : b.id,
    }))
  }

  return []
})


const setCurrentDepthFromProduct = () => {
  currentDepth1.value = null;
  currentDepth2.value = null;
  currentBrand.value = null;

  // const sessionHighCategoryCd = sessionStorage.getItem("product.highCategoryCd")??''
  // const sessionCategoryCd = sessionStorage.getItem("product.categoryCd")??''
  // const sessionBrandCd = sessionStorage.getItem("product.brandCd")??''

  // if(sessionHighCategoryCd != '')  currentDepth1.value = JSON.parse(sessionHighCategoryCd)
  // if(sessionCategoryCd != '') currentDepth2.value = JSON.parse(sessionCategoryCd)
  // if(sessionBrandCd) currentBrand.value = JSON.parse(sessionBrandCd)
  const foundDepth1 = treeData.value.find(v => v.id == searchHighCate.value)

  if (!foundDepth1) return

  currentDepth1.value = foundDepth1

  const foundDepth2 = foundDepth1.children.find((v: { id: string; }) => v.id === searchCate.value)

  if (foundDepth2) {
    currentDepth2.value = foundDepth2

    const foundBrand = foundDepth2.brands.find(
      (b: Brand) => b.id === searchBrand.value
    )

    if (foundBrand) {
      currentBrand.value = foundBrand
    }
  } else {
    const foundBrand = foundDepth1.brands.find(
      (b: Brand) => b.id === searchBrand.value
    )

    if (foundBrand) {
      currentBrand.value = foundBrand
    }
  }
}

const getDepth = (breadcrumb : BreadcrumbItem) => {
  // sessionStorage.removeItem("product.highCategoryCd")
  // sessionStorage.removeItem("product.categoryCd")
  // sessionStorage.removeItem("product.brandCd")

  const foundDepth1 = treeData.value.find(v => v.id == breadcrumb.highCategoryId)

  if (!foundDepth1) return

  currentDepth1.value = foundDepth1

  const foundDepth2 = foundDepth1.children.find((v: { id: string; }) => v.id === breadcrumb.categoryId)

  if (foundDepth2) {
    currentDepth2.value = foundDepth2

    const foundBrand = foundDepth2.brands.find(
      (b: Brand) => b.id === breadcrumb.brandId
    )

    if (foundBrand) {
      currentBrand.value = foundBrand
    }
  } else {
    const foundBrand = foundDepth1.brands.find(
      (b: Brand) => b.id === breadcrumb.brandId
    )

    if (foundBrand) {
      currentBrand.value = foundBrand
    }
  }

  // if (breadcrumb.depth ===1) {
  //   sessionStorage.setItem("product.highCategoryCd", JSON.stringify(currentDepth1.value))
  // } else if (breadcrumb.depth ===2) {
  //   sessionStorage.setItem("product.highCategoryCd", JSON.stringify(currentDepth1.value))
  //   sessionStorage.setItem("product.categoryCd",JSON.stringify(currentDepth2.value))
  // } else if (breadcrumb.depth ===3) {
  //   sessionStorage.setItem("product.highCategoryCd", JSON.stringify(currentDepth1.value))
  //   sessionStorage.setItem("product.categoryCd",JSON.stringify(currentDepth2.value))
  //   sessionStorage.setItem("product.brandCd", JSON.stringify(currentBrand.value))
  // }
}

// 브래드 크럼 메뉴
const breadcrumbs = computed<BreadcrumbItem[]>(() => {
// 홈 경로
const result : BreadcrumbItem[] = [{label: '홈', to:'/pc/product/main'}]

if (currentDepth1.value) {
    result.push({
      label: currentDepth1.value.name,
      to:currentDepth1.value.to,
      child: depth1Menus.value,
      id: currentDepth1.value.id,
      depth: 1,
      highCategoryId: currentDepth1.value.id
    })
  }

  if (currentDepth2.value) {
    result.push({
      label: currentDepth2.value.name,
      to:currentDepth2.value.to,
      child: depth2Menus.value,
      id: currentDepth2.value.id,
      depth: 2,
      highCategoryId: currentDepth1.value?.id??'',
      categoryId: currentDepth2.value.id
    })
  }

  if(currentBrand.value) {
    result.push({
      label: currentBrand.value.name,
      to:currentBrand.value.to,
      child: depth3Menus.value,
      id: currentBrand.value.id,
      depth: 3,
      highCategoryId: currentDepth1.value?.id??'',
      categoryId: currentDepth2.value?.id??'', 
      brandId: currentBrand.value.id
    })
  }

  return result
})

const searchCate = ref("")
const searchHighCate = ref("")
const searchBrand = ref("")

const getProductDetailInformation = async () => {
      productDetail.value.dspGdsCd = ''
      productDetail.value.frstCtgrCd = ''
      productDetail.value.sndCtgrCd = ''
      productDetail.value.brndCd = ''
      setCurrentDepthFromProduct();
}

const getQuery = (v: any) => {
  if(v === undefined || v === null) return ''
  return Array.isArray(v) ? v[0] : String(v)
}

watch(() => route.fullPath, async (newVal, oldVal) => {

  searchCate.value = ""
  searchHighCate.value = ""
  searchBrand.value = ""

  searchCate.value = getQuery(route.query.searchCategory)
  searchHighCate.value = getQuery(route.query.searchHighCategory)
  searchBrand.value = getQuery(route.query.searchBrand)

  // if(!searchHighCate.value && !searchCate.value && !searchBrand.value) {
  // sessionStorage.removeItem("product.highCategoryCd")
  // sessionStorage.removeItem("product.categoryCd")
  // sessionStorage.removeItem("product.brandCd")
  // }
  await getProductDetailInformation()
})

onMounted(async () => {
await fetchBuilder.get<AjaxResult<DisplayMainHeaderInfo>>('/api/v1/product/display/main-header')
  .progressMessage("잠시만 기다려주세요...")
  .build()
  .execute()
  .then(async (result) => {
    if(result.resultCode != '200') {
      showAlert(result.resultMessage);
    } else {
      // 카테고리-브랜드 매핑 세팅
      categoryBrandList.value = result.data.ctgrBrndMappingList;
      searchCate.value = ""
      searchHighCate.value = ""
      searchBrand.value = ""

      searchCate.value = getQuery(route.query.searchCategory)
      searchHighCate.value = getQuery(route.query.searchHighCategory)
      searchBrand.value = getQuery(route.query.searchBrand)
      await getProductDetailInformation();
    }
});

  // searchCate.value = getQuery(route.query.searchCategory)
  // searchHighCate.value = getQuery(route.query.searchHighCategory)
  // searchBrand.value = getQuery(route.query.searchBrand)
})
// pc/product/promotion 브래드크럼 영역
definePageMeta({
isDetail: true,
});

</script>